import React, { useState, useEffect, Suspense, lazy } from 'react';
import { publicApi } from '../api';

const PDFExportButton = lazy(() => import('../components/PDFExportButton'));

const getClickRate = (count) => {
  if (count >= 10000) return 1.00;
  if (count >= 5000)  return 1.50;
  if (count >= 500)   return 2.50;
  return 3.00;
};

const fmt = (n) => `$${(n ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtSigned = (n) => (n >= 0 ? '+' : '') + fmt(n);

const STEP_EMAIL    = 0;
const STEP_VERIFY   = 1;
const STEP_COLLECT  = 2;
const STEP_REPORT   = 3;

const SESSION_KEY = 'ss_session_id';

export default function SelfService() {
  const [step, setStep]           = useState(STEP_EMAIL);
  const [email, setEmail]         = useState('');
  const [sessionId, setSessionId] = useState('');
  const [otp, setOtp]             = useState('');
  const [uploadFiles, setUploadFiles] = useState([]);
  const [report, setReport]       = useState(null);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');
  const [status, setStatus]       = useState('');

  // Restore session from URL or localStorage on load
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlSession = params.get('session');
    const stored = urlSession || localStorage.getItem(SESSION_KEY);
    if (stored) {
      setSessionId(stored);
      if (urlSession) {
        localStorage.setItem(SESSION_KEY, urlSession);
      }
      publicApi.get(`/public/sessions/${stored}`)
        .then(({ data }) => {
          if (data.status === 'report_generated' && data.report) {
            setReport(data.report);
            setStep(STEP_REPORT);
          } else if (data.status === 'verified') {
            setStep(STEP_COLLECT);
          } else if (data.status === 'email_pending') {
            setStep(STEP_VERIFY);
          }
        })
        .catch(() => {
          localStorage.removeItem(SESSION_KEY);
        });
    }
  }, []);

  const handleStart = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { data } = await publicApi.post('/public/sessions', { email });
      setSessionId(data.sessionId);
      localStorage.setItem(SESSION_KEY, data.sessionId);
      setStep(STEP_VERIFY);
      if (data.resumed) setStatus('We already sent you a code — check your inbox.');
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await publicApi.post('/public/sessions/verify', { sessionId, code: otp });
      setStep(STEP_COLLECT);
    } catch (err) {
      setError(err.response?.data?.error || 'Verification failed. Check your code and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setError('');
    setLoading(true);
    try {
      const { data } = await publicApi.post('/public/sessions', { email });
      setSessionId(data.sessionId);
      localStorage.setItem(SESSION_KEY, data.sessionId);
      setOtp('');
      setError('');
      setStatus(data.resumed ? 'Your original code is still valid — check your inbox.' : 'A new code has been sent.');
    } catch (err) {
      setError(err.response?.data?.error || 'Could not resend code.');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadFiles.length) return;
    setError('');
    setLoading(true);
    setStatus('Analyzing your WorkSpaces data…');
    try {
      const texts = await Promise.all(uploadFiles.map(f => f.text()));
      const packages = texts.map(t => JSON.parse(t));
      const payload = packages.length === 1 ? packages[0] : packages;
      const { data } = await publicApi.post(`/public/sessions/${sessionId}/data`, payload);
      setReport(data.report);
      setStep(STEP_REPORT);
      setStatus('Inspection complete. Ready for departure.');
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Upload failed.';
      setError(msg);
      setStatus('');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    localStorage.removeItem(SESSION_KEY);
    setStep(STEP_EMAIL);
    setEmail('');
    setSessionId('');
    setOtp('');
    setReport(null);
    setError('');
    setStatus('');
    // Strip ?session= from URL without reload
    window.history.replaceState({}, '', '/try');
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--color-bg, #f5f7fa)', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <header style={{ width: '100%', background: '#000000', padding: '1rem 2rem', display: 'flex', alignItems: 'center', gap: 12 }}>
        <img src="/logo-navbar.png" alt="" style={{ height: 28 }} aria-hidden="true" />
        <span style={{ color: '#fff', fontWeight: 600, fontSize: 18 }}>Click Pre-Flight Inspection</span>
      </header>

      <main style={{ width: '100%', maxWidth: 640, padding: '2rem 1rem', flex: 1 }}>
        {step === STEP_EMAIL && (
          <div className="card">
            <h2 style={{ marginTop: 0 }}>Analyze your WorkSpaces cost</h2>
            <p style={{ color: 'var(--color-text-secondary)', marginBottom: '1.5rem' }}>
              Run a free analysis to see how much you could save on AWS WorkSpaces — no account needed.
              Enter your work email to get started.
            </p>
            <form onSubmit={handleStart}>
              <div className="form-group">
                <label htmlFor="ss-email">Work email</label>
                <input
                  id="ss-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  required
                  autoFocus
                />
              </div>
              {error && <p style={{ color: 'var(--color-red)', fontSize: 14 }}>{error}</p>}
              <button type="submit" disabled={loading} className="btn-success" style={{ width: '100%' }}>
                {loading ? 'Sending code…' : 'Get started'}
              </button>
            </form>
          </div>
        )}

        {step === STEP_VERIFY && (
          <div className="card">
            <h2 style={{ marginTop: 0 }}>Check your email</h2>
            <p style={{ color: 'var(--color-text-secondary)', marginBottom: '1.5rem' }}>
              We sent a 6-digit verification code to <strong>{email}</strong>. Enter it below.
            </p>
            <form onSubmit={handleVerify}>
              <div className="form-group">
                <label htmlFor="ss-otp">Verification code</label>
                <input
                  id="ss-otp"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                  placeholder="123456"
                  required
                  autoFocus
                  style={{ letterSpacing: '0.2em', fontSize: 20, maxWidth: 160 }}
                />
              </div>
              {error && <p style={{ color: 'var(--color-red)', fontSize: 14 }}>{error}</p>}
              {status && <p style={{ color: 'var(--color-green)', fontSize: 14 }}>{status}</p>}
              <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                <button type="submit" disabled={loading || otp.length !== 6}>
                  {loading ? 'Verifying…' : 'Verify'}
                </button>
                <button type="button" className="btn-secondary" onClick={handleResend} disabled={loading}>
                  Resend code
                </button>
              </div>
            </form>
          </div>
        )}

        {step === STEP_COLLECT && (
          <div className="card">
            <h2 style={{ marginTop: 0 }}>Collect your WorkSpaces data</h2>

            <div style={{ background: '#F4F4F4', borderRadius: 6, padding: '1rem 1.25rem', marginBottom: '1.5rem' }}>
              <p style={{ margin: '0 0 0.5rem', fontWeight: 600 }}>Step 1 — Run the PowerShell script</p>
              <p style={{ margin: '0 0 0.75rem', fontSize: 14, color: 'var(--color-text-secondary)' }}>
                The script reads WorkSpaces inventory and usage data from your AWS account using your existing credentials. It does not make any changes.
              </p>
              <a href="/scripts/Get-WorkSpacesData.ps1" download="Get-WorkSpacesData.ps1" className="btn-secondary btn-sm">
                Download Get-WorkSpacesData.ps1
              </a>
              <pre style={{ marginTop: '0.75rem', background: '#F4F4F4', borderRadius: 4, padding: '0.5rem 0.75rem', fontSize: 12, overflowX: 'auto' }}>
                {`# In PowerShell (with AWS credentials configured)\n.\\Get-WorkSpacesData.ps1 -Region us-east-1`}
              </pre>
            </div>

            <div style={{ borderTop: '1px solid var(--color-border)', paddingTop: '1.25rem' }}>
              <p style={{ margin: '0 0 0.75rem', fontWeight: 600 }}>Step 2 — Upload the output file</p>
              <form onSubmit={handleUpload}>
                <input
                  type="file"
                  accept=".json"
                  multiple
                  onChange={(e) => setUploadFiles([...e.target.files])}
                  style={{ width: 'auto', marginBottom: '0.5rem' }}
                />
                {uploadFiles.length > 1 && (
                  <p style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)', margin: '0 0 0.5rem' }}>
                    {uploadFiles.length} files selected — will be merged into one report
                  </p>
                )}
                {error && <p style={{ color: 'var(--color-red)', fontSize: 14 }}>{error}</p>}
                {status && <div className="status-bar">{status}</div>}
                <button type="submit" disabled={loading || !uploadFiles.length} className="btn-success">
                  {loading ? 'Analyzing…' : 'Run Pre-Flight'}
                </button>
              </form>
            </div>
          </div>
        )}

        {step === STEP_REPORT && report && (
          <ReportView report={report} onReset={handleReset} />
        )}
      </main>
    </div>
  );
}

function ReportView({ report, onReset }) {
  const wsCount       = report.totalWorkspaces ?? 0;
  const clickRate     = getClickRate(wsCount);
  const clickMonthly  = wsCount * clickRate;
  const clickAnnual   = clickMonthly * 12;
  const hasSavings    = report.estimatedMonthlySavings != null;
  const totalSavings  = hasSavings
    ? (report.estimatedMonthlySavings ?? 0) + (report.idleFindings?.savingsMonthlyUsd ?? 0)
    : null;
  const netMonthly    = totalSavings != null ? totalSavings - clickMonthly : null;
  const roi           = netMonthly != null && clickMonthly > 0 ? (netMonthly / clickMonthly) * 100 : null;
  const netColor      = netMonthly != null ? (netMonthly >= 0 ? 'var(--color-green)' : 'var(--color-red)') : undefined;
  const dateStr       = new Date().toISOString().slice(0, 10);

  return (
    <>
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
          <h2 style={{ marginTop: 0 }}>Your WorkSpaces Analysis</h2>
          <Suspense fallback={<button disabled>Loading PDF…</button>}>
            <PDFExportButton
              report={report}
              customerName="Your Organization"
              fileName={`click-preflight-${dateStr}.pdf`}
            />
          </Suspense>
        </div>
        <p style={{ color: 'var(--color-text-secondary)', fontSize: 13, marginTop: 0 }}>
          A copy has been sent to your email. You can also download the executive summary PDF above.
        </p>

        <table>
          <tbody>
            <tr>
              <td><strong>Total WorkSpaces</strong></td>
              <td>{wsCount}</td>
            </tr>
            <tr>
              <td><strong>Estimated Monthly Cost</strong></td>
              <td>{fmt(report.currentMonthlyCost)}</td>
            </tr>
            <tr>
              <td><strong>Estimated Monthly Savings</strong></td>
              <td>
                {totalSavings != null
                  ? <span style={{ color: 'var(--color-green)', fontWeight: 600 }}>{fmt(totalSavings)}</span>
                  : <span style={{ color: 'var(--color-text-secondary)', fontStyle: 'italic' }}>Requires usage data</span>}
              </td>
            </tr>
            <tr>
              <td><strong>Estimated Annual Savings</strong></td>
              <td>
                {totalSavings != null
                  ? <span style={{ color: 'var(--color-green)', fontWeight: 600 }}>{fmt(totalSavings * 12)}</span>
                  : <span style={{ color: 'var(--color-text-secondary)', fontStyle: 'italic' }}>Requires usage data</span>}
              </td>
            </tr>
          </tbody>
        </table>

        {report.savingsNote && (
          <div style={{ background: 'rgba(29, 238, 161, 0.08)', border: '1px solid #1deea1', borderLeft: '3px solid #1deea1', borderRadius: 'var(--radius-sm)', padding: '0.6rem 0.75rem', marginTop: '1rem', fontSize: 13, color: '#064E3B' }}>
            {report.savingsNote}
          </div>
        )}
      </div>

      <div className="card" style={{ marginTop: '1rem' }}>
        <h3 style={{ marginTop: 0 }}>ROI — Click vs. Savings</h3>
        <table>
          <tbody>
            <tr>
              <td><strong>Click cost ({wsCount} × ${clickRate.toFixed(2)}/mo)</strong></td>
              <td><span style={{ color: 'var(--color-red)' }}>{fmt(clickMonthly)}/mo</span></td>
              <td><span style={{ color: 'var(--color-red)', fontSize: 13 }}>{fmt(clickAnnual)}/yr</span></td>
            </tr>
            <tr>
              <td><strong>Total savings identified</strong></td>
              <td>
                {totalSavings != null
                  ? <span style={{ color: 'var(--color-green)' }}>{fmt(totalSavings)}/mo</span>
                  : <span style={{ color: 'var(--color-text-secondary)', fontStyle: 'italic' }}>Requires usage data</span>}
              </td>
              <td>{totalSavings != null && <span style={{ color: 'var(--color-green)', fontSize: 13 }}>{fmt(totalSavings * 12)}/yr</span>}</td>
            </tr>
            <tr style={{ borderTop: '2px solid var(--color-border)' }}>
              <td><strong>Net savings after Click</strong></td>
              <td>
                {netMonthly != null
                  ? <span style={{ color: netColor, fontWeight: 600 }}>{fmtSigned(netMonthly)}/mo</span>
                  : <span style={{ color: 'var(--color-text-secondary)', fontStyle: 'italic' }}>Requires usage data</span>}
              </td>
              <td>{netMonthly != null && <span style={{ color: netColor, fontWeight: 600, fontSize: 13 }}>{fmtSigned(netMonthly * 12)}/yr</span>}</td>
            </tr>
            {roi != null && (
              <tr>
                <td><strong>ROI</strong></td>
                <td colSpan={2}>
                  <span style={{ color: roi >= 0 ? 'var(--color-green)' : 'var(--color-red)', fontWeight: 600, fontSize: 15 }}>
                    {roi >= 0 ? '+' : ''}{roi.toFixed(0)}%
                  </span>
                  <span style={{ color: 'var(--color-text-secondary)', fontSize: 12, marginLeft: 8 }}>
                    {roi >= 0 ? 'savings exceed Click cost' : 'savings do not yet cover Click cost'}
                  </span>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {report.lifecycleEvents && (
        <div className="card" style={{ marginTop: '1rem' }}>
          <h3 style={{ marginTop: 0 }}>
            Operational Activity
            <span style={{ fontWeight: 'normal', fontSize: 13, color: 'var(--color-text-secondary)', marginLeft: 8 }}>
              last {Math.min(report.lifecycleEvents.periodDays ?? 90, 90)} days
            </span>
          </h3>
          <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
            {[
              { label: 'Created',    value: report.lifecycleEvents.created,    color: '#1801B4' },
              { label: 'Terminated', value: report.lifecycleEvents.terminated, color: 'var(--color-red)' },
              { label: 'Modified',   value: report.lifecycleEvents.modified,   color: '#5B49DE' },
              { label: 'Rebuilt',    value: report.lifecycleEvents.rebuilt,    color: '#5B49DE' },
            ].map(({ label, value, color }) => (
              <div key={label} style={{ textAlign: 'center', minWidth: 80 }}>
                <div style={{ fontSize: 34, fontWeight: 600, color, lineHeight: 1.1 }}>{value ?? 0}</div>
                <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginTop: 4 }}>{label}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
        <button className="btn-secondary btn-sm" onClick={onReset}>Start a new analysis</button>
      </div>
    </>
  );
}
